import { h } from '@omni-torn/ui';
import { csvValue, excelCsv, keyLink, KEY_LEVEL_NAMES, keyMeets, keyNeedText, type KeyNeed } from '@omni-torn/shared';
import { session } from './session';

type Kid = Node | string | null | undefined | false;

export const money = (n: number | null | undefined): string => (n === null || n === undefined ? '—' : `$${Math.round(n).toLocaleString('en-US')}`);
export const num = (n: number | null | undefined, digits = 0): string => (n === null || n === undefined || !Number.isFinite(n) ? '—' : n.toLocaleString('en-US', { maximumFractionDigits: digits }));
/** 12 345 678 → "12.3m" */
export function short(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  const a = Math.abs(n);
  const [d, s] = a >= 1e12 ? [1e12, 't'] : a >= 1e9 ? [1e9, 'b'] : a >= 1e6 ? [1e6, 'm'] : a >= 1e3 ? [1e3, 'k'] : [1, ''];
  return `${(n / d).toLocaleString('en-US', { maximumFractionDigits: d === 1 ? 0 : a / d >= 100 ? 0 : 1 })}${s}`;
}
export const signed = (n: number | null, f: (n: number) => string = (x) => num(x)): string => (n === null ? '—' : n > 0 ? `+${f(n)}` : n < 0 ? `−${f(-n)}` : '±0');

/** Seconds → "2h 05m" / "45m" / "now". */
export function duration(sec: number | null | undefined): string {
  if (!sec || sec <= 0) return 'now';
  const d = Math.floor(sec / 86_400);
  const hr = Math.floor((sec % 86_400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d) return `${d}d ${hr}h`;
  if (hr) return `${hr}h ${String(m).padStart(2, '0')}m`;
  return m ? `${m}m` : `${Math.ceil(sec)}s`;
}

/** Torn time (TCT = UTC) for a unix-ms moment: "14:05" today, else "Sat 14:05". */
export function tct(ms: number): string {
  const d = new Date(ms);
  const t = `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
  const today = new Date();
  return d.toDateString() === today.toDateString() && d.getUTCDate() === today.getUTCDate() ? t : `${d.toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' })} ${t}`;
}

export function ago(ms: number): string {
  const s = Math.max(0, (Date.now() - ms) / 1000);
  if (s < 60) return 'just now';
  return `${duration(s)} ago`;
}

/** A Torn-style box with a header bar. */
export function box(title: string | Node, body: Kid | Kid[], opts: { extra?: Kid; cls?: string; id?: string } = {}): HTMLElement {
  return h(
    'section',
    { class: `panel ${opts.cls ?? ''}`.trim(), ...(opts.id ? { 'data-card': opts.id } : {}) },
    h('header', { class: 'panel-head' }, typeof title === 'string' ? h('h2', {}, title) : title, opts.extra ?? null),
    h('div', { class: 'panel-body' }, ...(Array.isArray(body) ? body : [body])),
  );
}

/** The padlock (inline SVG, follows the text colour). */
export function lockIcon(): SVGSVGElement {
  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('viewBox', '0 0 16 16');
  svg.setAttribute('width', '12');
  svg.setAttribute('height', '12');
  svg.setAttribute('aria-hidden', 'true');
  svg.classList.add('lock-icon');
  const path = document.createElementNS(ns, 'path');
  path.setAttribute('fill', 'currentColor');
  path.setAttribute('d', 'M4 7V5a4 4 0 1 1 8 0v2h.5A1.5 1.5 0 0 1 14 8.5v5a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 2 13.5v-5A1.5 1.5 0 0 1 3.5 7H4Zm2 0h4V5a2 2 0 1 0-4 0v2Z');
  svg.append(path);
  return svg;
}

/** "Make a Limited Access key" link to Torn's own key page (opens in a new tab). */
export function keyMakeLink(level: 1 | 2 | 3 | 4, text?: string): HTMLAnchorElement {
  return h('a', { href: keyLink(level), target: '_blank', rel: 'noopener noreferrer', class: 'key-link' }, text ?? `Make a ${KEY_LEVEL_NAMES[level]} key ↗`);
}

/** A card the key can't read: greyed out, padlock, which key it needs and how to get one. */
export function lockedCard(title: string, need: KeyNeed, what: string): HTMLElement {
  const key = session.get()?.access ?? null;
  return h(
    'section',
    { class: 'panel locked', 'data-locked': 'key' },
    h('header', { class: 'panel-head' }, h('h2', {}, lockIcon(), ` ${title}`)),
    h('div', { class: 'panel-body' }, h('p', { class: 'lock-text' }, `${keyNeedText(key, need)} — ${what}`), h('p', {}, keyMakeLink(need.level), ' · then ', h('a', { href: '/account#key', 'data-link': true }, 'add it here'))),
  );
}

/** Build the card if the key can read it, else the locked version. */
export function gated(title: string, need: KeyNeed, what: string, build: () => HTMLElement): HTMLElement {
  return keyMeets(session.get()?.access ?? null, need) ? build() : lockedCard(title, need, what);
}

export function loading(text = 'Loading…'): HTMLElement {
  return h('div', { class: 'loading', 'aria-busy': 'true' }, text);
}

export function problem(err: unknown): HTMLElement {
  return h('div', { class: 'notice bad', role: 'alert' }, err instanceof Error ? err.message : 'Something went wrong.');
}

/** A meter like Torn's bars (energy green, nerve red, happy yellow, life blue). */
export function meter(label: string, current: number, maximum: number, kind: string, note?: string): HTMLElement {
  const fill = h('div', { class: `meter-fill meter-${kind}` });
  fill.style.width = `${Math.max(0, Math.min(100, maximum > 0 ? (current / maximum) * 100 : 0))}%`;
  return h('div', { class: 'meter' }, h('div', { class: 'meter-top' }, h('span', {}, label), h('span', {}, `${num(current)} / ${num(maximum)}`)), h('div', { class: 'meter-track' }, fill), note ? h('div', { class: 'meter-note muted' }, note) : null);
}

/** Label / value rows. */
export function facts(rows: Array<[string, Kid]>): HTMLElement {
  return h('dl', { class: 'facts' }, ...rows.flatMap(([k, v]) => [h('dt', {}, k), h('dd', {}, v ?? '—')]));
}

/** Sort choices of tables drawn again with the same `remember` name (pages that refresh by themselves). */
const sortKept = new Map<string, { sortBy: number; desc: boolean }>();

/**
 * A sortable table: click a header to sort by it. Every one gets a "CSV" button (S40: export on every table, Excel-friendly)
 * that saves the rows as shown — `csv: false` where the page already has its own export of the same rows (all of them,
 * past the 500 shown), or a file name to use (else the box's title and today's date).
 */
export function table<T>(rows: readonly T[], cols: Array<{ label: string; get: (r: T) => Kid; sort?: (r: T) => number | string; cls?: string }>, opts: { empty?: string; initial?: number; desc?: boolean; remember?: string; csv?: false | string } = {}): HTMLElement {
  const kept = opts.remember ? sortKept.get(opts.remember) : undefined;
  let sortBy = kept?.sortBy ?? opts.initial ?? -1;
  let desc = kept?.desc ?? opts.desc ?? true;
  const tbody = h('tbody');
  const draw = () => {
    const list = [...rows];
    const col = cols[sortBy];
    if (col?.sort) {
      const f = col.sort;
      list.sort((a, b) => {
        const x = f(a);
        const y = f(b);
        const c = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
        return desc ? -c : c;
      });
    }
    tbody.replaceChildren(...list.map((r) => h('tr', {}, ...cols.map((c) => h('td', { class: c.cls ?? '' }, c.get(r) ?? '—')))));
    if (!list.length) tbody.append(h('tr', {}, h('td', { colspan: cols.length, class: 'muted' }, opts.empty ?? 'Nothing here yet.')));
  };
  const heads = cols.map((c, i) => {
    const th = h('th', { class: `${c.cls ?? ''}${c.sort ? ' sortable' : ''}`.trim(), scope: 'col' }, c.label);
    if (c.sort) {
      th.tabIndex = 0;
      const go = () => {
        desc = sortBy === i ? !desc : true;
        sortBy = i;
        if (opts.remember) sortKept.set(opts.remember, { sortBy, desc });
        heads.forEach((x, j) => x.setAttribute('aria-sort', j === sortBy ? (desc ? 'descending' : 'ascending') : 'none'));
        draw();
      };
      th.addEventListener('click', go);
      th.addEventListener('keydown', (e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), go()));
    }
    return th;
  });
  draw();
  const wrap = h('div', { class: 'table-wrap' }, h('table', {}, h('thead', {}, h('tr', {}, ...heads)), tbody));
  return opts.csv === false || !rows.length ? wrap : withTableCsv(wrap, opts.csv);
}

// ---------------------------------------------------------------- CSV from any table (S40)

/** A table as CSV rows: the header row(s) from <thead>, then each body row; form fields give their value. */
export function tableCsvRows(t: HTMLTableElement): { header: string[]; rows: Array<Array<string | number>> } {
  const cellText = (c: Element): string => {
    // A cell may say what it holds for a spreadsheet (a heatmap's colour → its number).
    const given = c.getAttribute('data-csv-value');
    if (given !== null) return given;
    const field = c.querySelector('input, select, textarea') as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null;
    if (field instanceof HTMLInputElement && (field.type === 'checkbox' || field.type === 'radio')) return field.checked ? 'yes' : 'no';
    if (field instanceof HTMLSelectElement) return field.selectedOptions[0]?.textContent ?? field.value;
    if (field) return field.value;
    // textContent, not innerText: a header styled in capitals stays as written.
    const text = c.textContent ?? '';
    return text.trim() ? text : (c.getAttribute('title') ?? '');
  };
  const headRow = t.tHead?.rows[t.tHead.rows.length - 1];
  const header = headRow ? [...headRow.cells].map((c) => cellText(c).replace(/\s+/g, ' ').trim()) : [];
  const rows: Array<Array<string | number>> = [];
  for (const body of t.tBodies) {
    for (const tr of body.rows) {
      // The "Nothing here yet." row isn't data.
      if (tr.cells.length === 1 && (tr.cells[0]!.colSpan > 1 || header.length > 1)) continue;
      rows.push([...tr.cells].map((c) => csvValue(cellText(c))));
    }
  }
  return { header, rows };
}

/** "omni-torn-<the box's title>-<today>.csv" */
function csvName(from: Element, given?: string): string {
  const title = given ?? from.closest('.panel')?.querySelector('.panel-head h2')?.textContent ?? 'table';
  const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 50) || 'table';
  return `omni-torn-${slug}-${new Date().toISOString().slice(0, 10)}.csv`;
}

/** A small "CSV" button that saves the table it sits with (Excel-friendly: numbers as numbers, UTF-8 mark, CRLF). */
export function tableCsvButton(find: () => HTMLTableElement | null, name?: string): HTMLButtonElement {
  const b = h('button', { type: 'button', class: 'btn-small btn-csv', 'data-table-csv': '', title: 'Save this table as a CSV file (opens in Excel or any spreadsheet)' }, '⤓ CSV') as HTMLButtonElement;
  b.addEventListener('click', () => {
    const t = find();
    if (!t) return;
    const { header, rows } = tableCsvRows(t);
    downloadText(csvName(b, name), excelCsv(header, rows));
  });
  return b;
}

/** Adds the CSV button to a `.table-wrap` (under its table; stays in view while the table scrolls sideways). */
export function withTableCsv(wrap: HTMLElement, name?: string): HTMLElement {
  wrap.append(h('div', { class: 'table-tools' }, tableCsvButton(() => wrap.querySelector('table'), name)));
  return wrap;
}

/** A little line chart (SVG, no libraries): points in time order. */
export function lineChart(points: ReadonlyArray<{ at: number; v: number }>, opts: { label: string; format?: (n: number) => string; height?: number; xFormat?: (ms: number) => string } = { label: '' }): HTMLElement {
  const fmt = opts.format ?? short;
  if (points.length < 2) return h('div', { class: 'chart-empty muted' }, points.length ? 'One point so far — the chart starts with the next one.' : 'No history yet.');
  const W = 600;
  const H = opts.height ?? 160;
  const pad = { l: 52, r: 10, t: 10, b: 22 };
  const xs = points.map((p) => p.at);
  const ys = points.map((p) => p.v);
  const [x0, x1] = [Math.min(...xs), Math.max(...xs)];
  let [y0, y1] = [Math.min(...ys), Math.max(...ys)];
  if (y0 === y1) [y0, y1] = [y0 - 1, y1 + 1];
  const X = (x: number) => pad.l + ((x - x0) / (x1 - x0 || 1)) * (W - pad.l - pad.r);
  const Y = (y: number) => pad.t + (1 - (y - y0) / (y1 - y0)) * (H - pad.t - pad.b);
  const ns = 'http://www.w3.org/2000/svg';
  const el = <K extends string>(tag: K, attrs: Record<string, string | number>) => {
    const e = document.createElementNS(ns, tag);
    for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, String(v));
    return e;
  };
  const svg = el('svg', { viewBox: `0 0 ${W} ${H}`, class: 'chart', role: 'img', 'aria-label': `${opts.label}: from ${fmt(ys[0]!)} to ${fmt(ys[ys.length - 1]!)}` });
  for (let i = 0; i <= 3; i++) {
    const v = y0 + ((y1 - y0) * i) / 3;
    svg.append(el('line', { x1: pad.l, x2: W - pad.r, y1: Y(v), y2: Y(v), class: 'chart-grid' }));
    const t = el('text', { x: pad.l - 6, y: Y(v) + 4, class: 'chart-axis', 'text-anchor': 'end' });
    t.textContent = fmt(v);
    svg.append(t);
  }
  for (const [x, anchor] of [
    [x0, 'start'],
    [x1, 'end'],
  ] as const) {
    const t = el('text', { x: X(x), y: H - 6, class: 'chart-axis', 'text-anchor': anchor });
    t.textContent = opts.xFormat ? opts.xFormat(x) : new Date(x).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    svg.append(t);
  }
  const d = points.map((p, i) => `${i ? 'L' : 'M'}${X(p.at).toFixed(1)},${Y(p.v).toFixed(1)}`).join(' ');
  svg.append(el('path', { d: `${d} L${X(x1).toFixed(1)},${H - pad.b} L${X(x0).toFixed(1)},${H - pad.b} Z`, class: 'chart-area' }));
  svg.append(el('path', { d, class: 'chart-line' }));
  const last = points[points.length - 1]!;
  svg.append(el('circle', { cx: X(last.at), cy: Y(last.v), r: 3, class: 'chart-dot' }));
  return h('div', { class: 'chart-wrap' }, svg);
}

/** A row of tabs that switch what's shown below (keyboard friendly). */
export function tabs(items: Array<{ id: string; label: string }>, current: string, onPick: (id: string) => void): HTMLElement {
  const bar = h('div', { class: 'seg', role: 'tablist' });
  for (const it of items) {
    const b = h('button', { type: 'button', role: 'tab', class: it.id === current ? 'active' : '', 'aria-selected': it.id === current ? 'true' : 'false' }, it.label);
    b.addEventListener('click', () => {
      for (const x of bar.children) {
        x.classList.toggle('active', x === b);
        x.setAttribute('aria-selected', x === b ? 'true' : 'false');
      }
      onPick(it.id);
    });
    bar.append(b);
  }
  return bar;
}

export function ext(href: string, text: string): HTMLAnchorElement {
  return h('a', { href, target: '_blank', rel: 'noopener noreferrer' }, text);
}

export const profileUrl = (id: number) => `https://www.torn.com/profiles.php?XID=${id}`;

/** For replaceChildren(...): drops the empty ones. */
export const kids = (...xs: Kid[]): Array<Node | string> => xs.filter((x): x is Node | string => !!x);

/** Hand the visitor a file made here (CSV and the like): a link to a blob, clicked once, then let go. */
export function downloadText(name: string, text: string, type = 'text/csv'): void {
  // CSV opens in Excel with the right letters only with a UTF-8 mark first (S40: Excel-friendly).
  const body = type === 'text/csv' && !text.startsWith('\uFEFF') ? `\uFEFF${text}` : text;
  downloadBlob(name, new Blob([body], { type: `${type};charset=utf-8` }));
}

/** Hand the visitor a file (a blob made here — a backup, a CSV): a link clicked once, then let go. */
export function downloadBlob(name: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = h('a', { href: url, download: name, hidden: true });
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
